import React, { useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const SanskritQuote = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [isHovered, setIsHovered] = useState(false);

  // Animated counter for visual interest
  const [animatedDots, setAnimatedDots] = useState(0);

  useEffect(() => {
    if (isInView) {
      const interval = setInterval(() => {
        setAnimatedDots((prev) => (prev + 1) % 4);
      }, 800);
      return () => clearInterval(interval);
    }
  }, [isInView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      rotate: [0, 1, 0, -1, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut" as const,
      },
    },
  };

  return (
    <section
      ref={ref}
      id="sanskrit-quote"
      className="relative py-24 lg:py-32 overflow-hidden bg-white mt-px"
    >
      {/* Content Container */}
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="max-w-7xl mx-auto"
        >
          {/* Section header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-soft-saffron/20 to-copper-brown/20 backdrop-blur-sm border border-soft-saffron/30 mb-6">
              <div className="w-2 h-2 rounded-full bg-soft-saffron animate-pulse"></div>
              <span className="text-sm font-medium text-forest-green uppercase tracking-wider">
                Sacred Wisdom
              </span>
              <div className="flex gap-1">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-1 h-1 rounded-full bg-copper-brown transition-opacity duration-300 ${
                      i <= animatedDots ? "opacity-100" : "opacity-30"
                    }`}
                  />
                ))}
              </div>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-forest-green mt-1 mb-4">
              Ancient Wisdom for{" "}
              <span className="bg-gradient-to-r from-soft-saffron to-copper-brown bg-clip-text text-transparent">
                Modern Life
              </span>
            </h2>
            <p className="text-copper-brown/80 text-lg max-w-2xl mx-auto">
              Timeless teachings from the Upanishads that illuminate the path to <br />
              inner freedom
            </p>
          </motion.div>

          {/* Main content grid */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            {/* Sanskrit Verse Card */}
            <motion.div
              variants={itemVariants}
              className="relative group h-full"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div className="relative h-full">
                {/* Glowing border effect */}
                <div
                  className={`absolute inset-0 bg-gradient-to-r from-soft-saffron via-copper-brown to-earth-ochre rounded-2xl p-[1px] transition-all duration-500 ${
                    isHovered ? "opacity-60 scale-[1.02]" : "opacity-30"
                  }`}
                >
                  <div className="h-full w-full bg-cloud-white rounded-2xl"></div>
                </div>

                {/* Main card */}
                <div className="relative bg-gradient-to-br from-cloud-white to-cloud-white/95 backdrop-blur-xl rounded-2xl p-8 lg:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-misty-sage/50 h-full flex flex-col justify-center">
                  {/* Sanskrit text */}
                  <div className="text-center">
                    <div className="relative mb-8">
                      {/* Decorative quote marks */}
                      <div className="absolute -top-4 -left-4 text-6xl text-soft-saffron/30 font-serif leading-none">
                        "
                      </div>
                      <div className="font-sanskrit text-2xl md:text-3xl lg:text-3xl leading-relaxed font-medium text-forest-green mb-6 relative">
                        <span className="block mb-2">
                          मन एव मनुष्याणां कारणं बन्धमोक्षयोः।
                        </span>
                        <span className="block">
                          बन्धाय विषयासक्तं मुक्त्यै निर्विषयं स्मृतम्॥
                        </span>
                      </div>
                      <div className="absolute -bottom-4 -right-4 text-6xl text-soft-saffron/30 font-serif leading-none rotate-180">
                        "
                      </div>
                    </div>

                    {/* Source attribution with modern styling */}
                    <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-misty-sage/50 to-sand-beige/50 backdrop-blur-sm border border-earth-ochre/30 mb-6">
                      <div className="w-8 h-[1px] bg-gradient-to-r from-transparent to-copper-brown"></div>
                      <span className="text-sm font-medium text-copper-brown tracking-wide">
                        Amṛtabindu Upaniṣad
                      </span>
                      <div className="w-8 h-[1px] bg-gradient-to-l from-transparent to-copper-brown"></div>
                    </div>
                    <p className="text-copper-brown/80 text-lg max-w-2xl mx-auto mb-2">
                      {" "}
                      Mana eva manuṣyāṇāṁ kāraṇaṁ bandhamokṣayoḥ Bandhāya
                      viṣayāsaktaṁ muktyai nirviṣayaṃ smṛtam{" "}
                    </p>

                    {/* Translation as plain text */}
                    <div className="text-center">
                      <p className="text-lg text-copper-brown/90 leading-relaxed max-w-2xl mx-auto">
                        "The mind alone is the cause of bondage and liberation.
                        When attached to sense objects, it leads to bondage;
                        when free from them, it leads to liberation."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Translation and Insight Card */}
            <motion.div variants={itemVariants} className="relative h-full">
              <div className="h-full">
                {/* Modern insight card */}
                <div className="bg-gradient-to-br from-sand-beige/80 to-misty-sage/60 backdrop-blur-xl rounded-xl p-8 lg:p-12 border border-earth-ochre/30 h-full flex flex-col justify-center">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-soft-saffron to-copper-brown flex items-center justify-center flex-shrink-0 mt-1">
                      <svg
                        className="w-6 h-6 text-cloud-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-forest-green mb-4">
                        Modern Application
                      </h4>
                      <div className="space-y-3 text-sm text-copper-brown leading-relaxed">
                        <p>
                          <strong>Bondage (bandha)</strong> or suffering is not
                          caused by the world outside, but by the mind's
                          attachments and identifications, especially to
                          pleasure, possessions and ego.
                        </p>
                        <p>
                          <strong>Liberation (moksha)</strong> is not
                          necessarily about renouncing the world but about
                          achieving inner detachment, where the mind no longer
                          craves or clings to external objects.
                        </p>
                        <p>
                          A mind free of desire <strong>(nirviṣaya)</strong>{" "}
                          becomes still, focused, and capable of realizing the
                          Self <strong>(Atman)</strong>, leading to ultimate
                          peace and freedom.
                        </p>
                        <p>
                          Our mind thus plays a central role in determining our
                          state of well-being and living a happy fulfilled life.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom decorative element */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center mt-16"
          >
            <div className="flex items-center gap-6">
              <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-soft-saffron to-transparent"></div>
              <div className="w-3 h-3 rounded-full bg-gradient-to-br from-soft-saffron to-copper-brown animate-pulse"></div>
              <div className="w-24 h-[1px] bg-gradient-to-l from-transparent via-soft-saffron to-transparent"></div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default SanskritQuote;
